import { useEffect, useMemo, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { ChatBoxRecipients } from './ChatBoxRecipients';
import { ChatBoxSuggestionsList } from './ChatBoxSuggestionsList';
import { ChatBoxMessages } from './ChatBoxMessages';
import { ChatBoxInput } from './ChatBoxInput';
import type { ContactSuggestion } from '../../types';
import { useChat } from '../../hooks';
import { useContactSuggestions } from '../../hooks/use-contact-suggestions';
import { useCreateConversation } from '../../hooks/use-create-conversation';

/**
 * The suggestions list, messages, and input for the new message chat box.
 * @see {@link ChatBox}
 */
export function ChatBoxNewMessage({
    className,
    isOpen,
}: {
    className?: string;
    /**
     * Whether the chat box is open.
     */
    isOpen: boolean;
}) {
    const [recipients, setRecipients] = useState<ContactSuggestion[]>([]);
    const { setPendingMessage, convertNewMessageToConversation } = useChat();
    const {
        suggestions,
        pageInfo: suggestionsPageInfo,
        isLoading: isLoadingSuggestions,
        search,
        setParams,
    } = useContactSuggestions({ initialLimit: 50 });
    const {
        conversation: conversationPreview,
        createdConversation,
        optimisticMessage,
        isLoading: isConversationLoading,
        isSubmitting: isCreatingConversation,
        createConversation,
    } = useCreateConversation({
        participantIDs: recipients.map((r) => r.ID),
    });
    const messages = useMemo(() => {
        const previewMessages = conversationPreview?.Messages.nodes ?? [];
        if (optimisticMessage) {
            return [optimisticMessage, ...previewMessages];
        }
        return previewMessages;
    }, [conversationPreview, optimisticMessage]);

    function addRecipient(suggestion: ContactSuggestion) {
        setRecipients((prev) => {
            if (prev.some((r) => r.ID === suggestion.ID)) {
                return prev;
            }
            // TODO: BC-3398: Add group chat feature.
            if (prev.length >= 1) {
                return prev;
            }
            return [...prev, suggestion];
        });
    }

    // Convert this new message chat box to a normal conversation chat box when
    // a new conversation is created.
    useEffect(() => {
        if (createdConversation) {
            convertNewMessageToConversation(Number(createdConversation.ID));
        }
    }, [createdConversation, convertNewMessageToConversation]);

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            <ChatBoxRecipients
                search={search}
                recipients={recipients}
                onSearchChange={(search) => {
                    setParams({ q: search, limit: search.trim() ? 100 : 50 });
                }}
                onRecipientsChange={setRecipients}
            />
            <div className="h-px bg-white"></div>
            {recipients.length === 0 ? (
                <ChatBoxSuggestionsList
                    className="min-h-0 flex-1"
                    isLoading={isLoadingSuggestions}
                    suggestions={suggestions}
                    hasMoreSuggestions={
                        !search.trim() && suggestionsPageInfo?.hasNextPage
                    }
                    onSuggestionClick={addRecipient}
                />
            ) : (
                <>
                    <ChatBoxMessages
                        className="min-h-0 flex-1"
                        conversation={conversationPreview ?? undefined}
                        messages={messages}
                        isLoading={isConversationLoading && !messages.length}
                    />
                    <ChatBoxInput
                        isOpen={isOpen}
                        isDisabled={isConversationLoading}
                        isSubmitting={isCreatingConversation}
                        onSend={({ message, attachments }) => {
                            if (conversationPreview) {
                                const conversationID = Number(
                                    conversationPreview.ID
                                );
                                setPendingMessage({
                                    conversationID,
                                    message,
                                    attachments,
                                });
                                convertNewMessageToConversation(conversationID);
                            } else {
                                // This new message chat box will be converted to
                                // a normal conversation chat box when this action
                                // is completed (see useEffect above).
                                createConversation(message, attachments);
                            }
                        }}
                    />
                </>
            )}
        </div>
    );
}
